import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { ep } from '../api/endpoints';

/**
 * 為替・株式分析の銘柄マスタ (mst_market_symbol) のデータ層フック。
 *
 * 銘柄管理ダイアログと、ページヘッダーの銘柄セレクタで利用する。
 * 新規登録はバックエンドで 5 年分の日足をバックフィルするため、数秒かかることがある。
 */

export type MarketAssetType = 'fx' | 'stock' | 'index';
export type MarketMarket = 'FX' | 'JP' | 'US';
export type MarketCurrency = 'JPY' | 'USD';

export type MarketCoverage = {
  symbol_code: string;
  rows_total: number;
  first_date: string | null;
  last_date: string | null;
  synced_at: string | null;
};

export type MarketSymbol = {
  symbol_code: string;
  display_name: string;
  asset_type: MarketAssetType;
  market: MarketMarket;
  currency: MarketCurrency;
  is_active: boolean;
  sort_no: number;
  created_by: string | null;
  created_at: string | null;
  updated_by: string | null;
  updated_at: string | null;
  coverage: MarketCoverage | null;
};

export type MarketSymbolInput = {
  symbol_code: string;
  display_name: string;
  asset_type: MarketAssetType;
  market: MarketMarket;
  currency: MarketCurrency;
  is_active: boolean;
  sort_no: number;
  operator_email?: string;
};

const LIST_URL = ep('get_market_symbols');
const UPSERT_URL = ep('upsert_market_symbol');
const DELETE_URL = ep('delete_market_symbol');

export const useMarketSymbols = () => {
  const [symbols, setSymbols] = useState<MarketSymbol[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(LIST_URL, {});
      if (res.data?.status === 'success') {
        setSymbols(res.data.symbols || []);
      } else {
        setError(res.data?.error || '銘柄一覧の取得に失敗しました');
      }
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || '銘柄一覧の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const save = useCallback(async (input: MarketSymbolInput) => {
    const res = await axios.post(UPSERT_URL, input);
    if (res.data?.status !== 'success') {
      throw new Error(res.data?.error || '保存に失敗しました');
    }
    await reload();
    return { symbolCode: res.data.symbol_code as string, isNew: !!res.data.is_new, backfilled: Number(res.data.backfilled || 0) };
  }, [reload]);

  const remove = useCallback(async (symbolCode: string) => {
    const res = await axios.post(DELETE_URL, { symbol_code: symbolCode });
    if (res.data?.status !== 'success') {
      throw new Error(res.data?.error || '削除に失敗しました');
    }
    await reload();
  }, [reload]);

  return { symbols, loading, error, reload, save, remove };
};
